import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import parse from 'html-react-parser'
import { chatApi } from '../../api/chat'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import './Chat.css'

// Helper function to detect HTML content
const containsHtml = (text) => {
  if (typeof text !== 'string') return false
  const htmlPattern = /<[^>]+>/
  return htmlPattern.test(text)
}

// Message Content Component - handles both HTML and Markdown
const MessageContent = ({ content, hasNoAnswer = false }) => {
  if (!content) return null
  
  const isHtml = containsHtml(content)
  
  if (isHtml) {
    return (
      <div className={`message-html-content ${hasNoAnswer ? 'no-answer' : ''}`}>
        {parse(content)}
      </div>
    )
  }
  
  return <ReactMarkdown>{content}</ReactMarkdown>
}

// Thinking Indicator Component
const ThinkingIndicator = () => {
  return (
    <div className="thinking-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  )
}

// Typewriter Message Component (only for non-HTML content)
const TypewriterMessage = ({ children, messageId, onComplete, hasNoAnswer = false }) => {
  const [displayedText, setDisplayedText] = useState('')
  const fullText = typeof children === 'string' ? children : ''
  const isHtml = containsHtml(fullText)

  useEffect(() => {
    if (isHtml || !fullText) {
      setDisplayedText(fullText)
      if (onComplete) onComplete()
      return
    }

    setDisplayedText('')
    let currentIndex = 0
    const interval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex + 1))
        currentIndex++
      } else {
        clearInterval(interval)
        if (onComplete) onComplete()
      }
    }, 20) // Adjust speed as needed

    return () => clearInterval(interval)
  }, [messageId, fullText, isHtml, onComplete])

  if (isHtml || !fullText) {
    return <MessageContent content={fullText} hasNoAnswer={hasNoAnswer} />
  }

  return <ReactMarkdown>{displayedText}</ReactMarkdown>
}

const Chat = () => {
  const [message, setMessage] = useState('')
  const [currentFlowId, setCurrentFlowId] = useState(null)
  const [messages, setMessages] = useState([])
  const [animatedMessageIds, setAnimatedMessageIds] = useState(new Set())
  const messagesEndRef = useRef(null)
  const queryClient = useQueryClient()
  const locale = localStorage.getItem('locale') || 'ar'

  const { data: flowsData, isLoading: flowsLoading } = useQuery({
    queryKey: ['chat-flows'],
    queryFn: () => chatApi.getFlows(),
  })

  const { data: knowledgeBaseQuestions = [], isLoading: kbLoading } = useQuery({
    queryKey: ['knowledge-base-questions', locale],
    queryFn: () => chatApi.getKnowledgeBaseQuestions('website', locale),
    enabled: true, // Always fetch
  })

  // Debug: Log knowledge base questions
  useEffect(() => {
    if (knowledgeBaseQuestions.length > 0) {
      console.log('Knowledge Base Questions loaded:', knowledgeBaseQuestions.length)
      console.log('Sample question:', knowledgeBaseQuestions[0])
    }
  }, [knowledgeBaseQuestions])

  // Clear messages on mount to start fresh
  useEffect(() => {
    setMessages([])
    setCurrentFlowId(null)
  }, [])

  const sendMutation = useMutation({
    mutationFn: (data) => chatApi.sendMessage(data),
    onSuccess: (response, variables) => {
      // Handle response format: { success: true, data: {...} } or direct data
      const responseData = response?.data || response

      // Add user message
      if (variables.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: 'user',
            message: variables.message,
            timestamp: new Date().toISOString(),
          },
        ])
      }

      // Add bot response
      if (responseData?.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            type: 'bot',
            message: responseData.message,
            hasAnswer: responseData.has_answer !== false,
            responses: responseData.responses || [],
            sourceType: responseData.source_type,
            actions: responseData.actions || [],
            searchResults: responseData.search_results,
            timestamp: new Date().toISOString(),
          },
        ])

        // Update flow ID if provided
        if (responseData.next_flow_id) {
          setCurrentFlowId(responseData.next_flow_id)
        }
      }
    },
    onError: (error) => {
      console.error('Chat error:', error)
      // Show error message to user
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'bot',
          message: locale === 'ar' 
            ? 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' 
            : 'Sorry, an error occurred. Please try again.',
          hasAnswer: false,
          timestamp: new Date().toISOString(),
        },
      ])
    },
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sendMutation.isPending])

  const flows = Array.isArray(flowsData) ? flowsData : flowsData?.flows || []

  const handleSend = (e) => {
    e.preventDefault()
    if (!message.trim() || sendMutation.isPending) return

    sendMutation.mutate({
      message: message.trim(),
      flowId: currentFlowId,
    })
    setMessage('')
  }

  const handleFrequentQuestion = (kbQuestion) => {
    if (sendMutation.isPending) return
    const questionId = kbQuestion?.id
    const questionText = kbQuestion?.question || kbQuestion
    
    sendMutation.mutate({
      message: questionText,
      flowId: currentFlowId,
      knowledgeBaseId: questionId,
    })
  }

  const handleFlowOption = (option) => {
    if (sendMutation.isPending) return
    const optionText = option.text || option.response_text || ''
    const nextFlowId = option.next_flow_id || option.action_value

    if (nextFlowId) {
      setCurrentFlowId(nextFlowId)
    }

    sendMutation.mutate({
      message: optionText,
      flowId: nextFlowId || currentFlowId,
    })
  }

  const handleActionClick = (action) => {
    // Handle action buttons (navigate to course, lecture, etc.)
    if (action.type === 'course') {
      window.location.href = `/courses/${action.id}`
    } else if (action.type === 'lecture' && action.course_id) {
      window.location.href = `/courses/${action.course_id}/lectures/${action.id}`
    } else if (action.type === 'post') {
      window.location.href = `/community/posts/${action.id || action.post_id}`
    }
  }

  const handleRestartChat = () => {
    if (sendMutation.isPending) return
    
    // Clear messages
    setMessages([])
    // Reset flow ID
    setCurrentFlowId(null)
    // Clear animated message IDs
    setAnimatedMessageIds(new Set())
    // Reload chat flows and knowledge base questions
    queryClient.invalidateQueries(['chat-flows'])
    queryClient.invalidateQueries(['knowledge-base-questions', locale])
    // Scroll to top
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Get root flow for initial display
  const rootFlow = flows.find((f) => f.is_root === true || f.is_root === 1) || flows[0]
  const frequentQuestions = knowledgeBaseQuestions || []

  return (
    <div className="chat-page bg-gray-900 min-h-screen">
      <Header />
      <main className="chat-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="chat-container">
            <div className="chat-header">
              <div className="chat-header-content">
                <div className="chat-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div>
                  <h2>{locale === 'ar' ? 'دعم الدردشة' : 'Chat Support'}</h2>
                  <p>{locale === 'ar' ? 'مساعد ذكي' : 'AI Assistant'}</p>
                </div>
              </div>
              <button
                className="chat-restart-button"
                onClick={handleRestartChat}
                disabled={sendMutation.isPending}
                title={locale === 'ar' ? 'بدء محادثة جديدة' : 'New Conversation'}
                aria-label={locale === 'ar' ? 'بدء محادثة جديدة' : 'New Conversation'}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M3 21v-5h5" />
                </svg>
              </button>
            </div>

            <div className="chat-messages">
              {flowsLoading && messages.length === 0 && (
                <div className="chat-loading">
                  <Loading />
                </div>
              )}

              {!flowsLoading && messages.length === 0 && rootFlow && (
                <div className="welcome-section">
                  {rootFlow.message && (
                    <div className="welcome-message">
                      <MessageContent content={rootFlow.message} />
                    </div>
                  )}

                  {frequentQuestions.length > 0 && (
                    <div className="frequent-questions">
                      <h3>{locale === 'ar' ? 'الأسئلة المتكررة' : 'Frequent Questions'}</h3>
                      <div className="frequent-questions-list">
                        {frequentQuestions.map((kbQuestion, idx) => {
                          // Handle both object format (from backend) and string format
                          const questionId = kbQuestion?.id
                          const questionText = kbQuestion?.question || kbQuestion
                          // Strip HTML tags for display in button
                          const displayText = typeof questionText === 'string' 
                            ? questionText.replace(/<[^>]+>/g, '').trim() 
                            : questionText
                          
                          return (
                            <button
                              key={questionId || idx}
                              className="frequent-question-chip"
                              onClick={() => handleFrequentQuestion(kbQuestion)}
                              disabled={sendMutation.isPending}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
                              </svg>
                              <span>{displayText}</span>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M5 12h14M12 5l7 7-7 7" />
                              </svg>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {rootFlow.responses && rootFlow.responses.length > 0 && (
                    <div className="flow-options">
                      <p>{locale === 'ar' ? 'اختر خيارًا:' : 'Select an option:'}</p>
                      {rootFlow.responses.map((response, idx) => (
                        <button
                          key={idx}
                          className="option-button"
                          onClick={() => handleFlowOption(response)}
                          disabled={sendMutation.isPending}
                        >
                          {response.text || response.response_text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`message ${msg.type === 'user' ? 'user-message' : 'bot-message'} ${
                    msg.hasAnswer === false ? 'no-answer' : ''
                  }`}
                >
                  {msg.sourceType === 'database_search' && (
                    <div className="message-source">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                      <span>{locale === 'ar' ? 'تم العثور عليه في قاعدة البيانات' : 'Found in database'}</span>
                    </div>
                  )}

                  <div className="message-content">
                    {msg.type === 'user' ? (
                      <p>{msg.message}</p>
                    ) : (
                      <>
                        {animatedMessageIds.has(msg.id) || containsHtml(msg.message) ? (
                          <MessageContent content={msg.message} hasNoAnswer={msg.hasAnswer === false} />
                        ) : (
                          <TypewriterMessage
                            key={`typewriter-${msg.id}`}
                            messageId={msg.id}
                            hasNoAnswer={msg.hasAnswer === false}
                            onComplete={() => {
                              if (msg.id) {
                                setAnimatedMessageIds((prev) => {
                                  const newSet = new Set(prev)
                                  newSet.add(msg.id)
                                  return newSet
                                })
                              }
                            }}
                          >
                            {msg.message}
                          </TypewriterMessage>
                        )}
                      </>
                    )}
                  </div>

                  {msg.actions && msg.actions.length > 0 && (
                    <div className="message-actions">
                      {msg.actions.map((action, actionIdx) => (
                        <button
                          key={actionIdx}
                          className="action-button"
                          onClick={() => handleActionClick(action)}
                        >
                          {action.title || action.type}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.hasAnswer === false && msg.responses && msg.responses.length > 0 && (
                    <div className="message-responses">
                      <p className="responses-label">
                        {locale === 'ar' ? 'يرجى اختيار خيار:' : 'Please select an option:'}
                      </p>
                      {msg.responses.map((response, respIdx) => (
                        <button
                          key={respIdx}
                          className="response-option"
                          onClick={() => handleFlowOption(response)}
                          disabled={sendMutation.isPending}
                        >
                          {response.text || response.response_text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {sendMutation.isPending && (
                <div className="message bot-message typing-indicator">
                  <ThinkingIndicator />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} className="chat-input-form">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={locale === 'ar' ? 'اكتب رسالة...' : 'Type a message...'}
                fullWidth
                disabled={sendMutation.isPending}
              />
              <Button
                type="submit"
                variant="primary"
                disabled={!message.trim() || sendMutation.isPending}
                loading={sendMutation.isPending}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                {locale === 'ar' ? 'إرسال' : 'Send'}
              </Button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Chat
